import React, {FC, useEffect, useState, ReactNode} from "react"

import Flex from "@irati/ui/src/Flex"
import {Body, H1, H6} from "@irati/ui/src/Typography"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {PaymentCurrencyType, StoryPersona} from "@irati/types"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import authentication from "@irati/auth"
import Button from "@irati/ui/src/Button"

import {LinearGradiantCard} from "./styled"

enum Personas {
  discountDiva = "diva",
  rockstar = "rockstar",
  engagementGurus = "guru",
  topSavers = "saver",
  engagementEnthusiast = "enthusiast"
}

interface PersonaCard {
  title: string
  image: string
  text: ReactNode | null
  c1: string
  c2: string
}

interface Props {
  currencyCode: PaymentCurrencyType
  data: StoryPersona
  onClick: () => void
}

const Persona: FC<Props> = ({currencyCode, data, onClick}) => {
  const [card, setCard] = useState<PersonaCard>()

  const businessName = authentication.business().businessName
  const title = card?.title
  const theme = useTheme()
  const {formatMessage, formatNumber} = useIntl()

  const getPersona = () => {
    const index = Object.values(data).findIndex((p) => p !== null)
    if (index > -1) {
      switch (Object.keys(data)[index]) {
        case Personas.discountDiva:
          return {
            title: "admin.components.yourPerkboxStory.persona.discount.title",
            image: "admin.components.yourPerkboxStory.persona.discount.img",
            text: (
              <FormattedMessage
                id="admin.components.yourPerkboxStory.persona.discount.text"
                values={{
                  unique: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatNumber(data.diva?.uniqueDeals!)}
                    </Body>
                  ),
                  redemptions: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatNumber(data.diva?.redemptions!)}
                    </Body>
                  ),
                  savings: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatCurrency({
                        isCents: false,
                        value: currencyCode === PaymentCurrencyType.Gbp ? data.diva?.totalSavingsGbp : data.diva?.totalSavingsAud,
                        currencyCode
                      })}
                    </Body>
                  )
                }}
              />
            ),
            c1: theme.colors.tertiary.i_400,
            c2: theme.colors.primary1
          }
        case Personas.rockstar:
          return {
            title: "admin.components.yourPerkboxStory.rockstar.title",
            image: "admin.components.yourPerkboxStory.rockstar.img",
            text: (
              <FormattedMessage
                id="admin.components.yourPerkboxStory.rockstar.text"
                values={{
                  engagementRate: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatNumber(data.rockstar?.engagementRate!)}
                    </Body>
                  ),
                  savings: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatCurrency({
                        isCents: false,
                        value: currencyCode === PaymentCurrencyType.Gbp ? data.rockstar?.totalSavingsGbp : data.rockstar?.totalSavingsAud,
                        currencyCode
                      })}
                    </Body>
                  )
                }}
              />
            ),
            c1: theme.colors.primary2,
            c2: theme.colors.primary1
          }
        case Personas.engagementGurus:
          return {
            title: "admin.components.yourPerkboxStory.engagementGurus.title",
            image: "admin.components.yourPerkboxStory.engagementGurus.img",
            text: (
              <FormattedMessage
                id="admin.components.yourPerkboxStory.engagementGurus.text"
                values={{businessName, engagementRate: formatNumber(data.guru?.engagementRate!)}}
              />
            ),
            c1: theme.colors.tertiary.i_400,
            c2: theme.colors.primary1
          }

        case Personas.topSavers:
          return {
            title: "admin.components.yourPerkboxStory.topSavers.title",
            image: "admin.components.yourPerkboxStory.topSavers.img",
            text: (
              <FormattedMessage
                id="admin.components.yourPerkboxStory.topSavers.text"
                values={{
                  businessName,
                  savings: (
                    <Body weight="bold" display="inline" color={theme.colors.greys.white}>
                      {formatCurrency({
                        isCents: false,
                        value: currencyCode === PaymentCurrencyType.Gbp ? data.saver?.totalSavingsGbp : data.saver?.totalSavingsAud,
                        currencyCode
                      })}
                    </Body>
                  )
                }}
              />
            ),
            c1: theme.colors.tertiary.f_400,
            c2: theme.colors.primary1_200
          }
        case Personas.engagementEnthusiast:
          return {
            title: "admin.components.yourPerkboxStory.engagementEnthusiast.title",
            image: "admin.components.yourPerkboxStory.engagementEnthusiast.img",
            text: <FormattedMessage id="admin.components.yourPerkboxStory.engagementEnthusiast.text" values={{businessName}} />,
            c1: theme.colors.primary1,
            c2: theme.colors.tertiary.b_200
          }
      }
    }
  }

  useEffect(() => {
    if (data) {
      setCard(getPersona())
    }
  }, [data])

  return (
    <>
      {card && (
        <LinearGradiantCard c1={card.c1} c2={card.c2}>
          <Flex alignItems="center" direction="column" gap={1}>
            <H6 color={theme.colors.greys.white}>
              <FormattedMessage id="admin.components.yourPerkboxStory.persona.title" />
            </H6>
            <img src={formatMessage({id: card.image})} width={150} height={150} />
            <H1 color={theme.colors.greys.white}>
              <FormattedMessage id={title} />
            </H1>
            <Body small color={theme.colors.greys.white} maxWidth="100%" textAlign="center">
              {card.text}
            </Body>
            <Button inverted onClick={onClick}>
              <FormattedMessage id="admin.components.yourPerkboxStory.persona.btn" />
            </Button>
          </Flex>
        </LinearGradiantCard>
      )}
    </>
  )
}

export default Persona
